<i18n>
{
  "fr": {
    "project-conformity-edit-btn": "Modifier",
    "project-conformity-cancel-btn": "Annuler",
    "project-conformity-last-update": "Dernière mise à jour:",
    "project-conformity-save-btn": "Sauvegarder",
    "project-conformity-status-label": "Statut du projet",
    "project-conformity-province-label" : "Province du projet",
    "compliance-title": "Indicateurs du projet",
    "template-compliance-title": "Indicateurs du gabarit",
    "general-compliance-print": "Imprimer le rapport",
    "error-project-conformity-project-name": "Le nom du projet ne peut être vide",
    "projects-list-title": "Conformité par projet",
    "pending-tasks-label": "Tâches à faire",
    "open-all-drawers": "Tout voir",
    "close-all-drawers": "Fermer les tiroirs"
  },
  "en": {
    "project-conformity-edit-btn": "Edit",
    "project-conformity-cancel-btn": "Cancel",
    "project-conformity-last-update": "Last update:",
    "project-conformity-save-btn": "Save",
    "project-conformity-status-label": "Project status",
    "project-conformity-province-label" : "Project province",
    "compliance-title": "Project indicators",
    "template-compliance-title": "Template indicators",
    "general-compliance-print": "Print the report",
    "error-project-conformity-project-name": "The project name cannot be empty",
    "projects-list-title": "Project compliance",
    "pending-tasks-label": "Tasks to do",
    "open-all-drawers": "See all",
    "close-all-drawers": "Close all"
  }
}
</i18n>

<template>
  <div>
    <s-loader :active="!project && $apollo.queries.project.loading" />
    <template v-if="project">
      <div class="card">
        <s-form ref="form" @submit="(ev) => applyNameEdited()">
          <!-- Bloc de titre -->
          <ui-heading background="primary" :isSpecific="!isProjectTemplate">
            <div v-show="!editName" class="level is-marginless">
              <div class="level-left" style="flex-shrink: 1">
                <h1
                  class="title is-marginless has-max-width-1024 has-text-weight-bold"
                  :class="{ 'is-4': $media.mobile, 'is-3': $media.tablet && !$media.widescreen, 'is-2': $media.widescreen }"
                >
                  {{ project.name }}
                </h1>
              </div>
              <div class="level-right">
                <span
                  v-if="lastUpdate"
                  :class="{ 'mb-2': $media.mobile, 'mr-2': $media.tablet }"
                  class="is-size-7 has-max-width-768 is-block"
                >
                  {{ lastUpdate }}
                </span>

                <b-tooltip
                  :label="$t('project-conformity-edit-btn')"
                  type="is-light"
                  :position="$media.mobile ? 'is-right' : 'is-left'"
                  class="is-hidden-print"
                  ><b-button
                    type="is-link"
                    icon-right="pen"
                    size="is-small"
                    native-type="button"
                    outlined
                    @click="toggleEditName(true, true)"
                    :title="$t('project-conformity-edit-btn')"
                  />
                </b-tooltip>
              </div>
            </div>
            <!-- Édition du titre -->
            <s-field type="is-white" v-show="editName" :value="nameEdited" :rules="rules.projectName" validate-on-input>
              <div class="level">
                <div class="level-left" style="flex-grow: 1">
                  <b-input v-model="nameEdited" expanded style="flex-grow: 1" />
                </div>
                <div
                  class="level-right"
                  :class="{
                    'mt-12px': $media.mobile,
                    'ml-12px': $media.tablet
                  }"
                >
                  <p class="level-item">
                    <b-button type="is-link" outlined expanded native-type="button" @click="toggleEditName(false, true)">
                      {{ $t('project-conformity-cancel-btn') }}
                    </b-button>
                  </p>
                  <p class="level-item">
                    <b-button type="is-link" outlined expanded native-type="submit">
                      {{ $t('project-conformity-save-btn') }}
                    </b-button>
                  </p>
                </div>
              </div>
            </s-field>
          </ui-heading>
        </s-form>

        <ui-box class="pb-0">
          <!--Spécifications d'un projet-->
          <project-specifications v-if="!isProjectTemplate" :data="project.stats">
            <div class="column">
              <!-- Province -->
              <s-field :label="$t('project-conformity-province-label')" custom-class="is-size-7 is-uppercase">
                <b-select expanded v-model="projectModel.provinceCode" @input="submitForm">
                  <option v-for="i in provinceCodes" :key="i.value" :value="i.value">{{ i.text }}</option>
                </b-select>
              </s-field>
            </div>
            <div class="column">
              <!-- Statut -->
              <s-field :label="$t('project-conformity-status-label')" custom-class="is-size-7 is-uppercase">
                <b-select expanded v-model="projectModel.status" @input="submitForm">
                  <option v-for="i in projectStatuses" :key="i.value" :value="i.value">{{ i.text }}</option>
                </b-select>
              </s-field>
            </div>
          </project-specifications>
          <!-- Progression du gabarit de projet -->
          <indicators-progress-bar :data="project.progressStats" v-else />
        </ui-box>

        <ui-box>
          <prismic-content :doctype="DOCUMENT_TYPE_PROJECTS_LIST" v-slot="{ content }">
            <b-message
              v-if="isProjectTemplate && $prismicHasText(content.project_template_helptext)"
              type="is-info"
              class="has-background-white-bis mb-5"
            >
              <prismic-rich-text :field="content.project_template_helptext" class="has-max-width-768" />
            </b-message>
            <b-message
              v-if="!isProjectTemplate && $prismicHasText(content.project_helptext)"
              type="is-info"
              class="has-background-white-bis mb-5"
            >
              <prismic-rich-text :field="content.project_helptext" class="has-max-width-768" />
            </b-message>
          </prismic-content>

          <div class="indicators-header level mb-0">
            <div class="indicators-header__title level-left">
              <h2 class="has-letter-spacing-wide is-size-7 has-text-grey is-uppercase is-marginless has-text-weight-medium">
                {{ isProjectTemplate ? $t('template-compliance-title') : $t('compliance-title') }}
              </h2>
            </div>
            <div class="level-right mb-32px is-hidden-print" style="flex-wrap: wrap; flex-shrink: 1">
              <div class="filter-indicators" v-if="!isProjectTemplate">
                <filter-specific-indicator @toggle-specific-indicators="toggleSpecificIndicators" />
              </div>
              <b-button
                type="is-link"
                outlined
                icon-left="print"
                @click="print"
                :class="{ 'mb-2': $media.mobile, 'ml-2': $media.tablet }"
              >
                {{ $t('general-compliance-print') }}
              </b-button>
              <b-button
                v-if="projectModel.status === $consts.enums.PROJECT_STATUS_ACTIVE"
                type="is-link"
                outlined
                icon-left="check-double"
                class="ml-2"
                tag="router-link"
                :to="{ name: $consts.urls.URL_PENDING_TASKS, query: { tab: 'projects', project: projectId } }"
              >
                {{ $t('pending-tasks-label') }}
              </b-button>
              <b-button type="is-link" outlined class="ml-2" @click="toggleAllDrawers()">
                <span v-if="allDrawersOpened">{{ $t('close-all-drawers') }}</span>
                <span v-if="!allDrawersOpened">{{ $t('open-all-drawers') }}</span>
              </b-button>
            </div>
          </div>

          <compliance
            ref="compliance"
            :data="project.compliance"
            :projectId="$route.params.projectId"
            :isProjectTemplate="project.isProjectTemplate"
            :showSpecificOnly="showSpecificOnly"
            :loading="$apollo.queries.project.loading"
            @drawers-state-updated="onDrawersStateUpdated"
            @reload-compliance="reloadCompliance"
          />
        </ui-box>
      </div>
    </template>
  </div>
</template>

<script>
import BusinessService from '@/services/business'
import ProvinceService from '@/services/province'
import { sendSignal } from '@/services/signals'
import i18n from '@/helpers/i18n'
import EnumHelper from '@/helpers/enums'
import FormHelper from '@/helpers/form'
import { URL_PROJECT_LIST } from '@/consts/urls'
import { PROJECT_STATUS_ACTIVE, PROJECT_STATUS_INACTIVE, PROJECT_STATUS_FINISH } from '@/consts/enums'
import { DOCUMENT_TYPE_PROJECTS_LIST } from '@/consts/prismic'

import Breadcrumbs from '@/mixins/breadcrumbs'

import Compliance from '@/components/indicator/compliance'
import FilterSpecificIndicator from '@/components/indicator/filter-specific-indicator'
import PrismicContent from '@/components/prismic/prismic-content'
import UiBox from '@/components/ui/box'
import UiHeading from '@/components/ui/heading'
import SForm from '@/components/form/form'
import SField from '@/components/form/field'
import ProjectSpecifications from '@/components/projects/project-specifications.vue'
import IndicatorsProgressBar from '@/components/projects/indicators-progress-bar.vue'

const PROJECT_STATUSES = [PROJECT_STATUS_ACTIVE, PROJECT_STATUS_INACTIVE, PROJECT_STATUS_FINISH]

export default {
  mixins: [Breadcrumbs],
  components: {
    Compliance,
    FilterSpecificIndicator,
    PrismicContent,
    UiBox,
    UiHeading,
    SForm,
    SField,
    ProjectSpecifications,
    IndicatorsProgressBar
  },
  data() {
    return {
      DOCUMENT_TYPE_PROJECTS_LIST,
      loading: true,
      editName: false,
      nameEdited: '',
      projectModel: null,
      rules: {
        projectName: [(value) => FormHelper.notEmpty(value) || this.$t('error-project-conformity-project-name')]
      },
      loaded: {
        apollo: false
      },
      allDrawersOpened: false,
      showSpecificOnly: false
    }
  },
  computed: {
    businessId() {
      return BusinessService.getCurrentBusiness().id
    },
    projectId() {
      return this.$route.params.projectId
    },
    isProjectTemplate() {
      return this.project && this.project.isProjectTemplate
    },
    breadcrumbs() {
      if (!this.project) return []

      return [
        {
          label: this.$t('projects-list-title'),
          url: { name: URL_PROJECT_LIST }
        },
        {
          label: this.project.name
        }
      ]
    },
    projectStatuses() {
      return PROJECT_STATUSES.map((x) => {
        return {
          value: x,
          text: EnumHelper.formatProjectStatus(x)
        }
      })
    },
    provinceCodes() {
      return ProvinceService.getAllProvinces()
    },
    lastUpdate() {
      if (!this.project || !this.project.updatedAt) return null

      const date = this.$format.dateTime(this.project.updatedAt)
      if (!this.project.updatedBy) {
        return `${this.$t('project-conformity-last-update')} ${date}`
      }

      const firstName = this.project.updatedBy.profile.firstName
      const lastName = this.project.updatedBy.profile.lastName
      return `${this.$t('project-conformity-last-update')} ${firstName} ${lastName} - ${date}`
    }
  },
  methods: {
    toggleEditName(show, reset) {
      this.editName = show
      if (reset) {
        this.nameEdited = this.project.name
      }
    },
    async applyNameEdited() {
      if (!this.$refs.form.validate()) return
      this.projectModel.name = this.nameEdited
      this.toggleEditName(false, false)
      await this.submitForm()
    },
    async submitForm() {
      if (!this.$refs.form.validate()) return

      this.loading = true

      try {
        await this.$apollo.mutate({
          mutation: this.$options.mutation.UpdateProject,
          variables: {
            input: {
              projectId: this.projectModel.id,
              name: this.projectModel.name,
              provinceCode: this.projectModel.provinceCode,
              status: this.projectModel.status
            }
          }
        })
        this.$success()
      } finally {
        this.loading = false
      }
    },
    print() {
      window.print()
    },
    toggleAllDrawers() {
      if (!this.allDrawersOpened) {
        this.$refs.compliance.openAll()
        this.allDrawersOpened = true
      } else if (this.allDrawersOpened) {
        this.$refs.compliance.closeAll()
        this.allDrawersOpened = false
      }
    },
    toggleSpecificIndicators(e) {
      e ? (this.showSpecificOnly = true) : (this.showSpecificOnly = false)
    },
    onDrawersStateUpdated({ allOpened }) {
      this.allDrawersOpened = allOpened
    },
    reloadCompliance() {
      this.$apollo.queries.project.refresh()
    }
  },
  apollo: {
    project: {
      query() {
        return this.$withFragments(this.$options.query.ProjectCompliance, Compliance.fragments.complianceFields)
      },
      variables() {
        return {
          projectId: this.projectId,
          language: i18n.contentLanguage()
        }
      },
      result({ data }) {
        if (data && data.project) {
          this.nameEdited = data.project.name
          this.projectModel = {
            id: data.project.id,
            name: data.project.name,
            provinceCode: data.project.provinceCode,
            status: data.project.status
          }
          this.loaded.apollo = true
        }
      }
    }
  },
  watch: {
    loaded: {
      handler: (value) => {
        if (value.apollo) {
          sendSignal('scroll')
        }
      },
      deep: true
    }
  }
}
</script>

<graphql>
query ProjectCompliance($projectId: ID!, $language: ContentLanguage!) {
  project(id: $projectId) {
    id
    name
    provinceCode
    isProjectTemplate
    status
    updatedAt
    updatedBy {
      id
      profile {
        id
        firstName
        lastName
      }
    }
    stats {
      directIndicatorsCount
      directIndicatorsCompletedCount
      directDocumentsCount
      directDocumentsCompletedCount
    }
    progressStats {
      doneCount
      inProgressCount
      lateCount
      todoCount
      notApplicableCount
    }
    compliance(language: $language) {
      ...complianceFields
    }
  }
}

mutation UpdateProject($input: UpdateProjectInput!) {
  updateProject(input: $input) {
    project {
      id
      name
      provinceCode
      status
      updatedAt
      updatedBy {
        id
        profile {
          id
          firstName
          lastName
        }
      }
    }
  }
}
</graphql>

<style lang="scss">
.indicators-header {
  display: flex;
  flex-direction: column-reverse;

  @include tablet {
    flex-direction: row;
  }

  &__title {
    align-self: flex-start;
    margin-bottom: $space-4px;

    @include tablet {
      align-self: flex-end;
    }
  }
}

.filter-indicators {
  margin-bottom: $space-8px;
  width: 100%;
  @include tablet {
    display: flex;
    justify-content: flex-end;
  }

  @include widescreen {
    width: auto;
    margin-bottom: 0;
  }
}
</style>
